import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setStatus("timeout"); return; }
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (err) {
        console.error("Payment status poll failed, retrying:", err);
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-16">
      <div className="text-center max-w-md" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Spinner size={56} className="text-[#2E523A] mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl font-bold">Confirming your payment…</h1>
            <p className="text-[#63675E] mt-2">This only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={72} weight="fill" className="text-[#2E523A] mx-auto mb-6" data-testid="payment-paid" />
            <h1 className="font-display text-4xl font-bold">Order confirmed!</h1>
            <p className="text-[#63675E] mt-3">Thank you for your purchase. Your gear is on the way to adventure.</p>
            <div className="flex gap-3 justify-center mt-8">
              <Link to="/shop"><Button className="rounded-full bg-[#2E523A] hover:bg-[#26442f]">Keep shopping</Button></Link>
              <Link to="/"><Button variant="outline" className="rounded-full">Home</Button></Link>
            </div>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle size={72} weight="fill" className="text-destructive mx-auto mb-6" data-testid="payment-failed" />
            <h1 className="font-display text-3xl font-bold">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
            <p className="text-[#63675E] mt-3">{status === "timeout" ? "Your payment is taking longer than expected. Check your email for confirmation." : "We couldn't confirm your payment. Please try again."}</p>
            <Link to="/cart"><Button className="rounded-full bg-[#2E523A] hover:bg-[#26442f] mt-8">Back to cart</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
